import React, { useRef, useEffect } from 'react';
import { Tag } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';

import { CalendarDTO, CalendarEvent, getEventTypeIcon, calendarStore } from 'entities/calendar';
import { formatDateRange } from 'shared/lib/dayjs';

import styles from './EventsList.module.scss';

dayjs.extend(isSameOrAfter);

const STANDARD_WORKING_HOURS = 8;

const EventsList = ({ filteredEvents }: { filteredEvents: CalendarDTO['events'] }): React.ReactElement => {
  const { t } = useTranslation('Calendar');

  const openedEventId = useStore(calendarStore, (state) => state.openedEventId);
  const setOpenedEventId = useStore(calendarStore, (state) => state.setOpenedEventId);
  const selectedYear = useStore(calendarStore, (state) => state.selectedYear);
  const selectedMonth = useStore(calendarStore, (state) => state.selectedMonth);

  const eventRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  useEffect(() => {
    if (openedEventId?.startDate && eventRefs.current[openedEventId.startDate]) {
      eventRefs.current[openedEventId.startDate]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [openedEventId, filteredEvents]);

  const handleEventClick = (event: CalendarEvent): void => {
    const eventStart = dayjs(event.startDate);
    const eventEnd = dayjs(event.endDate);
    const monthStart = dayjs().year(selectedYear).month(selectedMonth).startOf('month');

    let clickedDate = eventStart.format('YYYY-MM-DD');

    if (eventStart.isBefore(monthStart, 'day') && eventEnd.isSameOrAfter(monthStart, 'day')) {
      clickedDate = monthStart.format('YYYY-MM-DD');
    }

    setOpenedEventId({ startDate: event.startDate, endDate: event.endDate, clickedDate });
  };

  const handleKeyPress = (event: CalendarEvent, e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleEventClick(event);
    }
  };

  return (
    <div className={styles.scrollableContainer}>
      <div className={styles.eventsListWrapper}>
        {filteredEvents.map((event) => (
          <button
            type="button"
            key={event.startDate}
            ref={(el) => {
              eventRefs.current[event.startDate] = el;
            }}
            className={classNames(styles.eventBlock, styles[event.exclusionType], {
              [styles.workingSelected]:
                openedEventId?.startDate === event.startDate && event.exclusionType === 'WORKING_DAY',
              [styles.nonWorkingSelected]:
                openedEventId?.startDate === event.startDate && event.exclusionType === 'NON_WORKING_DAY',
            })}
            onClick={() => handleEventClick(event)}
            onKeyDown={(e) => handleKeyPress(event, e)}
            aria-label={event.name}
          >
            <div className={styles.detailsBlock}>
              <div>{getEventTypeIcon(event.exclusionType)}</div>
              <div className={styles.truncatedText}>
                {formatDateRange({ startDate: event.startDate, endDate: event.endDate })}
              </div>
              {event.exclusionType === 'WORKING_DAY' &&
              event.workingHours &&
              event.workingHours !== STANDARD_WORKING_HOURS ? (
                <Tag color="blue" bordered className={styles.countTag}>
                  <span>
                    {event.workingHours}
                    {t('calendarsTable.CalendarsList.hours.text')}
                  </span>
                </Tag>
              ) : null}
            </div>
            <div className={classNames(styles.truncatedText, styles.eventName)}>{event.name}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default EventsList;
